import { useCallback, useState } from 'react'
import type { ImageSlot } from '@/features/ai/images/lib/targetKeys'

export type DrawMode = 'auto' | 'manual'

/**
 * Режим диалога отрисовки и план ручного режима.
 *
 * `manualSeen` — «в ручной режим уже заходили»: строки уроков тянут блоки
 * каждая своим запросом (§9 — десять уроков это десять запросов), поэтому
 * пикер монтируется ЛЕНИВО, при первом переключении. Обратно он НЕ
 * размонтируется: выбор автора живёт внутри пикера, и тернарник по режиму
 * молча стирал бы отметки на каждый заход в «Нарисовать всё» и назад.
 *
 * Слот, с которого открыли диалог, задаёт и стартовый режим: авто-режим рисует
 * ТОЛЬКО исходный слот курса (§5.3), поэтому открыть диалог слотом второго
 * языка можно лишь в ручном режиме.
 */
export const useDrawMode = (initialSlot?: ImageSlot) => {
  const initial: DrawMode = initialSlot ? 'manual' : 'auto'
  // Режим ВЫВОДИТСЯ из пропа, пока автор не трогал переключатель (`picked ===
  // null`), а не замораживается на монтировании: диалог смонтирован всегда
  // (`EditorShell`), а слот приезжает позже — кнопкой из итога перевода.
  // С `useState(initial)` такое открытие вставало бы в авто-режим, который
  // картинок второго языка не рисует вовсе (§5.3).
  const [picked, setPicked] = useState<DrawMode | null>(null)
  const [seen, setSeen] = useState(false)
  const [plan, setPlan] = useState<string[]>([])
  const mode = picked ?? initial
  const manualSeen = seen || initial === 'manual'

  const pick = useCallback((next: DrawMode) => {
    setPicked(next)
    if (next === 'manual') setSeen(true)
  }, [])

  // Закрытие диалога размонтирует его содержимое вместе с выбором внутри
  // пикера — режим и план обязаны вернуться туда же, иначе следующее открытие
  // застанет ручной режим с планом прошлого раза.
  const reset = useCallback(() => {
    setPicked(null)
    setPlan([])
    setSeen(false)
  }, [])

  return { mode, pick, plan, setPlan, manualSeen, reset }
}
